import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';

import { CampaignConversionCard } from './CampaignConversionCard';
import { CampaignGuidelinesSummary } from './CampaignGuidelinesSummary';
import { CampaignInitialMessages } from './CampaignInitialMessages';
import { CampaignOverviewCard } from './CampaignOverviewCard';
import { getCampaignStatusLabelKey, getCampaignStatusVariant } from './campaignStatusBadge';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSenders } from '../../hooks/useCampaignSenders';
import { CampaignWrapper } from '../CampaignWrapper';

/**
 * Campaign analytics ("Overview" tab) — performance metrics plus the campaign's
 * initial messages and, for agentic campaigns, the AI agent guidelines.
 * Shared across campaign types (delegated to from unified/Analytics).
 */
export const CampaignAnalytics = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();

  const { campaign, getStatus } = useCampaigns(campaignId);
  const { brain } = useBrains(campaign?.brain_parent_id);
  const { campaign_senders, isSendersLoading } = useCampaignSenders(campaignId);

  const isLoading = getStatus === 'pending';

  useTitle(t('pages.campaigns_summary', { 0: campaign?.name ?? '-' }));

  useSegments(PageName.CAMPAIGN_ANALYTICS);

  return (
    <PageContentWrapper>
      <CampaignWrapper>
        {/* Header: campaign name + status badge */}
        <div className="mb-6 flex items-center gap-3">
          {isLoading ? (
            <Skeleton className="h-8 w-64" />
          ) : (
            <>
              <h1 className="text-2xl font-bold">{campaign?.name || ''}</h1>
              {campaign?.status && (
                <Badge
                  type="secondary"
                  flavour={getCampaignStatusVariant(campaign.status)}
                  rounded={false}
                >
                  {t(getCampaignStatusLabelKey(campaign.status))}
                </Badge>
              )}
            </>
          )}
        </div>

        {/* Overview + Conversion grey panel */}
        <div className="flex flex-col gap-6 rounded-xl bg-bg-muted p-1 lg:flex-row">
          <CampaignOverviewCard
            campaign={campaign}
            brain={brain}
            senders={campaign_senders}
            isLoading={isLoading}
          />
          <CampaignConversionCard campaign={campaign} isLoading={isLoading} />
        </div>

        {/* Initial messages */}
        <CampaignInitialMessages senders={campaign_senders} isLoading={isSendersLoading} />

        {/* Campaign guidelines (agentic campaigns only) */}
        <CampaignGuidelinesSummary campaign={campaign} brain={brain} />
      </CampaignWrapper>
    </PageContentWrapper>
  );
};
